/**
 * Realtor Enquiries (#332) — people who reached the realtor outside Finishd
 * (WhatsApp, a call, a referral, a walk-in), logged by the realtor. Each has a
 * chat the realtor keeps, and can be converted into a lead or dismissed.
 *
 * `?open=<enquiryId>` opens that enquiry's chat sheet; opening and closing the
 * sheet writes the parameter, so the dashboard can link straight to a chat.
 */

import { Inbox, MessageSquare, Plus, RefreshCw, UserCheck } from "lucide-react";
import { type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { SkeletonRows } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ConvertEnquiryDialog } from "@/features/realtor/components/enquiries/convert-enquiry-dialog";
import { EnquiryChatSheet } from "@/features/realtor/components/enquiries/enquiry-chat-sheet";
import { ChannelPill } from "@/features/realtor/components/enquiries/enquiry-shared";
import { LogEnquiryDialog } from "@/features/realtor/components/enquiries/log-enquiry-dialog";
import { useEnquiries } from "@/features/realtor/hooks/use-realtor-crm";
import type {
	Enquiry,
	EnquiryStatus,
	EnquirySummary,
} from "@/features/realtor/services/realtor-crm-api.service";
import { formatRelativeTime } from "@/features/realtor/utils/relative-time";
import { StudioButton } from "@/features/studio/theme";

const STATUSES: readonly EnquiryStatus[] = ["open", "converted", "dismissed"];

export function RealtorEnquiriesPage(): ReactElement {
	const { t } = useTranslation();
	const [searchParams, setSearchParams] = useSearchParams();
	const [status, setStatus] = useState<EnquiryStatus>("open");
	const [logOpen, setLogOpen] = useState(false);
	const [convertTarget, setConvertTarget] = useState<Enquiry | null>(null);
	const enquiries = useEnquiries({ status });
	const openId = searchParams.get("open");

	const setOpenId = (id: string | null): void => {
		setSearchParams(
			(prev) => {
				const next = new URLSearchParams(prev);
				if (id) next.set("open", id);
				else next.delete("open");
				return next;
			},
			{ replace: id == null },
		);
	};

	return (
		<div className="min-h-screen space-y-6 bg-dashboard-surface p-4 sm:p-6">
			<Card className="rounded-xl border-dashboard-border bg-dashboard-bg shadow-[var(--dashboard-shadow-2)]">
				<CardContent className="flex flex-col gap-4 p-5 sm:flex-row sm:items-start sm:justify-between sm:p-6">
					<div className="min-w-0">
						<p className="font-mono text-xs uppercase tracking-widest text-dashboard-muted">
							{t("realtor.enquiries.eyebrow")}
						</p>
						<h1
							className="mt-2 font-display text-3xl font-medium italic text-dashboard-accent-dark"
							style={{ lineHeight: 1.1 }}
						>
							{t("realtor.enquiries.title")}
						</h1>
						<p className="mt-2 text-sm text-dashboard-muted">
							{t("realtor.enquiries.subtitle")}
						</p>
						<p className="mt-1 text-xs text-dashboard-muted">
							{t("realtor.enquiries.websiteNote")}
						</p>
					</div>
					<StudioButton
						size="sm"
						className="shrink-0 self-start"
						onClick={() => setLogOpen(true)}
					>
						<Plus className="h-4 w-4" />
						{t("realtor.enquiries.logCta")}
					</StudioButton>
				</CardContent>
			</Card>

			<Tabs
				value={status}
				onValueChange={(value) => setStatus(value as EnquiryStatus)}
			>
				<TabsList
					aria-label={t("realtor.enquiries.tabsLabel")}
					className="h-auto w-full justify-start gap-1 border border-dashboard-border bg-dashboard-bg sm:w-auto"
				>
					{STATUSES.map((value) => (
						<TabsTrigger
							key={value}
							value={value}
							className="flex-1 text-dashboard-muted data-[state=active]:bg-dashboard-accent data-[state=active]:text-dashboard-on-fill sm:flex-none"
						>
							{t(`realtor.enquiries.tabs.${value}`)}
						</TabsTrigger>
					))}
				</TabsList>
			</Tabs>

			{enquiries.isPending ? (
				<SkeletonRows
					label={t("realtor.enquiries.loading")}
					rows={4}
					rowClassName="h-24 rounded-xl bg-dashboard-bg"
				/>
			) : enquiries.isError ? (
				<Card className="border-dashboard-border bg-dashboard-bg">
					<CardContent className="space-y-3 p-6 text-center" role="alert">
						<p className="text-sm text-dashboard-muted">
							{enquiries.error.message}
						</p>
						<StudioButton
							variant="secondary"
							size="sm"
							disabled={enquiries.isFetching}
							onClick={() => enquiries.refetch()}
						>
							<RefreshCw className="h-4 w-4" />
							{t("realtor.enquiries.retry")}
						</StudioButton>
					</CardContent>
				</Card>
			) : enquiries.data.length === 0 ? (
				<EmptyState status={status} onLog={() => setLogOpen(true)} />
			) : (
				<ul className="space-y-2">
					{enquiries.data.map((summary) => (
						<EnquiryRow
							key={summary.enquiry.id}
							summary={summary}
							onOpenChat={() => setOpenId(summary.enquiry.id)}
							onConvert={() => setConvertTarget(summary.enquiry)}
						/>
					))}
				</ul>
			)}

			<LogEnquiryDialog
				open={logOpen}
				onOpenChange={setLogOpen}
				onLogged={() => setStatus("open")}
			/>
			<EnquiryChatSheet
				enquiryId={openId}
				onClose={() => setOpenId(null)}
				onConvert={setConvertTarget}
			/>
			<ConvertEnquiryDialog
				enquiry={convertTarget}
				onOpenChange={(open) => {
					if (!open) setConvertTarget(null);
				}}
			/>
		</div>
	);
}

function EnquiryRow({
	summary,
	onOpenChat,
	onConvert,
}: {
	summary: EnquirySummary;
	onOpenChat: () => void;
	onConvert: () => void;
}): ReactElement {
	const { t, i18n } = useTranslation();
	const { enquiry, listing, firstMessage, messageCount } = summary;
	return (
		<li
			className="space-y-2 rounded-xl border border-dashboard-border bg-dashboard-bg p-4"
			data-testid={`enquiry-row-${enquiry.id}`}
		>
			<div className="flex flex-wrap items-center gap-x-2 gap-y-1">
				<p className="min-w-0 truncate text-sm font-semibold text-dashboard-text">
					{enquiry.name}
				</p>
				<ChannelPill channel={enquiry.channel} />
				<span className="ml-auto shrink-0 text-[11px] text-dashboard-muted">
					{formatRelativeTime(enquiry.createdAt, i18n.language)}
				</span>
			</div>
			{listing && (
				<p className="truncate text-xs text-dashboard-muted-alt">
					{listing.headline} · {listing.locality}
				</p>
			)}
			<p className="line-clamp-2 break-words text-sm text-dashboard-text">
				{firstMessage?.body ?? t("realtor.enquiries.row.noMessage")}
			</p>
			<div className="flex flex-wrap items-center gap-2 pt-1">
				<span className="inline-flex items-center gap-1 text-[11px] text-dashboard-muted">
					<MessageSquare className="h-3 w-3" />
					{t("realtor.enquiries.row.messageCount", { count: messageCount })}
				</span>
				<div className="ml-auto flex flex-wrap gap-2">
					<StudioButton
						variant="secondary"
						size="xs"
						onClick={onOpenChat}
						aria-label={t("realtor.enquiries.row.openChatAria", {
							name: enquiry.name,
						})}
					>
						{t("realtor.enquiries.row.openChat")}
					</StudioButton>
					{enquiry.status === "open" && (
						<StudioButton
							size="xs"
							onClick={onConvert}
							aria-label={t("realtor.enquiries.row.convertAria", {
								name: enquiry.name,
							})}
						>
							<UserCheck className="h-4 w-4" />
							{t("realtor.enquiries.convertCta")}
						</StudioButton>
					)}
				</div>
			</div>
		</li>
	);
}

function EmptyState({
	status,
	onLog,
}: {
	status: EnquiryStatus;
	onLog: () => void;
}): ReactElement {
	const { t } = useTranslation();
	return (
		<Card className="border-dashboard-border bg-dashboard-bg">
			<CardContent className="space-y-3 p-8 text-center">
				<Inbox className="mx-auto h-5 w-5 text-dashboard-accent-dark" />
				<h2 className="text-sm font-semibold text-dashboard-text">
					{t(`realtor.enquiries.empty.${status}.title`)}
				</h2>
				<p className="mx-auto max-w-md text-xs text-dashboard-muted">
					{t(`realtor.enquiries.empty.${status}.body`)}
				</p>
				{status === "open" && (
					<StudioButton variant="secondary" size="sm" onClick={onLog}>
						<Plus className="h-4 w-4" />
						{t("realtor.enquiries.logCta")}
					</StudioButton>
				)}
			</CardContent>
		</Card>
	);
}
